import { Playbook } from '@chilipiper/api-client/src/chat-platform-builder'
import {
  GuestStateResponseDto,
  LocationConditionEvaluationTree,
} from '@chilipiper/api-client/src/chat-platform-chat'
import { useApiClient } from '@chilipiper/api-client/src/context'
import { useFireSessionContext } from '@chilipiper/fire-session'
import { defaultErrorCallback } from '@chilipiper/service/src/api'
import { ApiError } from '@chilipiper/service/src/types'
import { isErrorCode } from '@chilipiper/utils'
import { queryOptions, useMutation, useQuery } from '@tanstack/react-query'
import { useCallback, useEffect } from 'react'
import DataLoader from 'dataloader'
import invariant from 'tiny-invariant'

export const useLocationSettings = ({
  workspaceId,
  tenantId,
  guestId,
}: {
  guestId?: string
  tenantId?: string
  workspaceId?: string
}) => {
  const { chatPlatformChat } = useApiClient()
  const query = useQuery({
    queryKey: ['location-settings', workspaceId, tenantId, guestId],
    queryFn: () => {
      invariant(
        workspaceId && tenantId && guestId,
        'workspaceId, tenantId and guestId are required'
      )
      return chatPlatformChat.getApiV1PublicTenantTenantidWorkspacesWorkspaceidLocationSettings(
        tenantId,
        workspaceId,
        {
          guestId,
        }
      )
    },
    enabled: Boolean(workspaceId && tenantId && guestId),
  })

  useEffect(() => {
    if (query.error) {
      defaultErrorCallback(query.error as unknown as ApiError)
    }
  }, [query.error])

  return query
}

export const usePlaybooksSelectWithConversation = (tenantId: string) => {
  const { chatPlatformChat } = useApiClient()
  return useMutation({
    mutationFn: ({
      guestId,
      playbookIds,
      workspaceId,
      targetedUrl,
      forceStartNextSession,
      locationEvaluation,
    }: {
      guestId: string
      playbookIds: string[]
      workspaceId: string
      targetedUrl: string
      forceStartNextSession?: boolean
      locationEvaluation?: Record<string, LocationConditionEvaluationTree>
    }) =>
      chatPlatformChat.putApiV1PublicTenantTenantidWorkspacesWorkspaceidPlaybooksSelectWithConversation(
        tenantId,
        workspaceId,
        {
          guestId,
          playbookIds,
          targetedUrl,
          locationEvaluation,
        },
        {
          forceStartNextSession,
        }
      ),
    onError: error => {
      if (isErrorCode(error, 'no-playbook-selected')) {
        // We shouldn't report error when no playbook was selected because we just keep
        return
      }
      defaultErrorCallback(error as unknown as ApiError)
    },
    retry: 3,
    retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000),
  })
}

export const usePrepareChat = (tenantId: string) => {
  const { chatPlatformChat } = useApiClient()
  return useMutation({
    mutationFn: ({ workspaceId, guestId }: { guestId: string; workspaceId: string }) =>
      chatPlatformChat.putApiV1PublicTenantTenantidWorkspacesWorkspaceidPrepareChat(
        tenantId,
        workspaceId,
        {
          guestId,
        }
      ),
    onError: error => {
      defaultErrorCallback(error as unknown as ApiError)
    },
  })
}

export const publishedPlaybookVersionKeys = (
  tenantId: string,
  workspaceId: string,
  playbookId: string
) => ['published-versions-playbook', tenantId, workspaceId, playbookId]

export const usePublishedPlaybookVersions = (
  workspaceId: string,
  playbookId: string,
  isEnabled = true
) => {
  const { tenantId } = useFireSessionContext()
  const { chatPlatformBuilder } = useApiClient()
  return useQuery<Playbook[]>({
    queryKey: publishedPlaybookVersionKeys(tenantId, workspaceId, playbookId),
    queryFn: () =>
      chatPlatformBuilder.getApiV1TenantTenantidWorkspacesWorkspaceidPlaybooksPublishedPlaybookid(
        tenantId,
        workspaceId,
        playbookId
      ),
    refetchOnWindowFocus: false,
    enabled: isEnabled,
  })
}

export const useMeetingQueryOptions = () => {
  const { meetings } = useApiClient()
  return (tenantId: string, meetingId: string) => {
    return queryOptions({
      queryKey: ['meeting', tenantId, meetingId],
      queryFn: () => meetings.getAPublicMeeting(tenantId, meetingId),
      enabled: Boolean(tenantId && meetingId),
    })
  }
}

export const useMeeting = (tenantId: string, meetingId?: string) => {
  const meetingQueryOptions = useMeetingQueryOptions()

  const query = useQuery({
    ...meetingQueryOptions(tenantId, meetingId as string),
    retry: 5,
    retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000),
  })

  useEffect(() => {
    if (query.error) {
      defaultErrorCallback(query.error as unknown as ApiError)
    }
  }, [query.error])

  return query
}

export const useChangeRoomStatus = (
  tenantId: string,
  roomId: string,
  { onError, onSuccess }: { onSuccess: (status: 'OPEN' | 'CLOSED') => void; onError: () => void }
) => {
  const { chatPlatformChat } = useApiClient()
  const { mutate, isPending: isLoading } = useMutation({
    mutationKey: ['chat-components', 'room-status', tenantId, roomId, 'mutation'],
    mutationFn: async (status: 'OPEN' | 'CLOSED') => {
      await chatPlatformChat.putApiV1TenantTenantidInboxManage(tenantId, {
        guestId: roomId,
        type: status === 'OPEN' ? 'OpenAllRelatedInboxes' : 'CloseAllRelatedInboxes',
      })
    },
  })
  return {
    isLoading,
    setRoomStatus: useCallback(
      (status: 'OPEN' | 'CLOSED') => {
        mutate(status, {
          onSuccess: () => {
            onSuccess(status)
          },
          onError,
        })
      },
      [mutate, onSuccess, onError]
    ),
  }
}

export const getGuestStateKey = (guestId: string, tenantId: string) => [
  'getGuestState',
  guestId,
  tenantId,
]

const loader = new DataLoader<
  { api: ReturnType<typeof useApiClient>['chatPlatformChat']; guestId: string; tenantId: string },
  GuestStateResponseDto | null
>(
  async keys => {
    invariant(keys[0], 'It is impossible to call this with no keys')
    const { api, tenantId } = keys[0] //TODO fix assumption that we have only one tenant or inject tenantId on loader creation
    //TODO would be more clean to provide api on loader creation and put loader inside some context, see loaders in platform assets common
    const result = await api.getApiV1TenantTenantidGuestState(tenantId, {
      guestIds: keys.map(k => k.guestId),
    })
    return keys.map(key => result[key.guestId] ?? null)
  },
  {
    cache: false,
  }
)
export const useGetGuestState = (
  tenantId: string,
  guestId?: string,
  refetchInterval?: number,
  { onSuccess }: { onSuccess?: (data: GuestStateResponseDto) => void } = {}
) => {
  const { chatPlatformChat } = useApiClient()
  const { data, isLoading, isError } = useQuery({
    queryKey: getGuestStateKey(guestId ?? '', tenantId),

    queryFn: () =>
      guestId !== undefined ? loader.load({ api: chatPlatformChat, guestId, tenantId }) : null,
    enabled: !!tenantId && !!guestId,
    refetchInterval,
  })

  useEffect(() => {
    if (data) {
      onSuccess?.(data)
    }
  }, [data])

  return {
    data,
    isLoading,
    isError,
  }
}
